import { useEffect, useRef, useState } from 'react'

import copy from 'copy-to-clipboard'
import { LoaderCircle } from 'lucide-react'
import moment from 'moment'
import queryString from 'query-string'
import { Button, DefaultPopover, DefaultTooltip, FormGroup, InputGroup, InputGroupText, Label, Select, Separator } from 'twyne-new.ui'

import InputCopyURL from '@/components/InputCopyURL'
import { useAppAlert } from '@Components/AppAlert'
import { useGlobalStore } from '@Stores/global'
import { formatDate, useQueryparams } from 'utils'
import history from 'utils/history'

import { PermanentLinkButton } from './header'

const MAX_DAYS = 120
const toOption = value => ({ label: value, value })
const OPTIONS = [1, 7, 30, 90].map(toOption)

const isValidDays = value => Number.isInteger(+value) && +value >= 1 && +value <= MAX_DAYS && !OPTIONS.some(option => option.value === +value)

const parseLegacy = value => {
  try {
    return JSON.parse(atob(value))
  } catch (e) {
    return null
  }
}

const buildLink = id => window.location.origin + history.createHref({
  ...history.location,
  search: queryString.stringify({ configure: id }),
})

export default function PermanentLink({ dumpState, onLoad, onError, inline = false }) {
  const { handleError } = useAppAlert()
  const { permanentLink } = useGlobalStore()
  const query = useQueryparams(true)
  const [days, setDays] = useState(7)
  const [generated, setGenerated] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pending, setPending] = useState(null)
  const consumed = useRef(null)

  const current = JSON.stringify(dumpState())
  const isGenerated = generated?.state === current

  useEffect(() => {
    if (!pending) return
    setGenerated({ ...pending, state: current })
    setPending(null)
  }, [pending, current])

  useEffect(() => {
    const configure = query?.configure
    if (inline || !configure || consumed.current === configure) return
    consumed.current = configure

    const legacy = parseLegacy(configure)
    if (legacy) return onLoad(legacy)

    const load = async () => {
      try {
        if (configure.startsWith('blob:')) {
          const response = await fetch(configure)
          return onLoad(await response.json())
        }

        const { configuration, expiresAt } = await permanentLink.load(configure)
        setPending({ link: buildLink(configure), expiresAt })
        onLoad(configuration)
      } catch (e) {
        if (onError) return onError()
        handleError('Error', 'Permanent link expired or not found.')
      }
    }
    load()
  }, [query, permanentLink, onLoad, onError, handleError])

  const handleGenerate = async () => {
    setLoading(true)
    try {
      const { id, expiresAt } = await permanentLink.create(JSON.parse(current), days)
      const link = buildLink(id)
      copy(link)
      setGenerated({ state: current, link, expiresAt })
    } catch (e) {
      handleError('Error', 'Permanent link was not generated.')
    }
    setLoading(false)
  }

  const content = <>
    <small className='block twy-text-neutral'>Saves the current filters and settings.</small>
    <Separator className='m-0 my-2' />
    <FormGroup>
      <Label>
        Expires in <span className='text-sm twy-text-neutral'>{formatDate(isGenerated ? generated.expiresAt : moment().add(days, 'days'), 'MMM D, YYYY')}</span>
      </Label>
      <div className='flex gap-2'>
        <InputGroup className='flex-1'>
          <Select
            creatable
            options={OPTIONS}
            value={toOption(days)}
            onChange={option => {
              setDays(+option.value)
              setGenerated(null)
            }}
            isValidNewOption={isValidDays}
            disabled={isGenerated || loading}
          />
          <InputGroupText addonType='append'>days</InputGroupText>
        </InputGroup>
        <DefaultTooltip content={isGenerated && 'Change the report settings to generate a new link'} triggerProps={{ asChild: true }}>
          <span className='flex'>
            <Button color='accent' onClick={handleGenerate} disabled={loading || isGenerated}>
              {loading && <LoaderCircle className='animate-spin' size={16} />}
              Generate
            </Button>
          </span>
        </DefaultTooltip>
      </div>
      {isGenerated && <div className='mt-1'>
        <InputCopyURL value={generated.link} label='link' />
      </div>}
    </FormGroup>
  </>

  if (inline) return content

  return <DefaultPopover trigger={<PermanentLinkButton />}>
    Permanent Link
    {content}
  </DefaultPopover>
}
